import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useLanguage } from '../context/LanguageContext'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { toast } from 'sonner'
import { 
  TrendingUp, TrendingDown, DollarSign, Plus, Settings, 
  RefreshCw, ChevronRight, Wallet, BarChart3, Pencil, Trash2
} from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts'
import { formatCurrencyDisplay } from '../components/CurrencyInput'

const API = process.env.REACT_APP_BACKEND_URL

export default function InvestmentsPage() {
  const navigate = useNavigate()
  const { t, locale, formatCurrency } = useLanguage()
  const isSpanish = locale.startsWith('es')
  
  const [dashboard, setDashboard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  
  // Category/Account management
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [showAccountModal, setShowAccountModal] = useState(false)
  const [categories, setCategories] = useState([])
  const [editingCategory, setEditingCategory] = useState(null)
  const [editingAccount, setEditingAccount] = useState(null)
  const [categoryForm, setCategoryForm] = useState({ nombre: '' })
  const [accountForm, setAccountForm] = useState({ nombre: '', categoria_id: '', moneda: 'MXN', nota: '' })
  
  // Multi-currency view state
  const [viewMode, setViewMode] = useState('separated') // 'separated' | 'consolidated'
  const [showExchangeModal, setShowExchangeModal] = useState(false)
  const [exchangeRate, setExchangeRate] = useState({ usd_to_mxn: 20.0, moneda_base: 'MXN' })
  
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/investments/dashboard`)
      setDashboard(response.data)
      // Update exchange rate from server
      if (response.data.tipo_cambio) {
        setExchangeRate(response.data.tipo_cambio)
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error)
      toast.error(isSpanish ? 'Error al cargar datos' : 'Error loading data')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [isSpanish])
  
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/investments/categories`)
      setCategories(response.data)
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }, [])
  
  useEffect(() => {
    fetchDashboard()
    fetchCategories()
  }, [fetchDashboard, fetchCategories])
  
  const handleRefresh = () => {
    setRefreshing(true)
    fetchDashboard()
    fetchCategories()
  }
  
  // Sparkline data with all months (for consolidated view) - shows VALUE evolution
  const sparklineData = useMemo(() => {
    if (!dashboard?.serie_mensual_anio_actual) return []
    
    const currentYear = new Date().getFullYear()
    const months = Array.from({ length: 12 }, (_, i) => {
      const month = String(i + 1).padStart(2, '0')
      return `${currentYear}-${month}`
    })
    
    return months.map(mes => {
      const found = dashboard.serie_mensual_anio_actual.find(m => m.mes === mes)
      return {
        mes: mes.split('-')[1],
        valor: found?.valor || 0
      }
    })
  }, [dashboard])
  
  // Sparkline data per currency - shows VALUE evolution
  const getSparklineForCurrency = useCallback((currency) => {
    if (!dashboard?.por_moneda?.[currency]?.serie) return []
    
    const currentYear = new Date().getFullYear()
    const months = Array.from({ length: 12 }, (_, i) => {
      const month = String(i + 1).padStart(2, '0')
      return `${currentYear}-${month}`
    })
    
    return months.map(mes => {
      const found = dashboard.por_moneda[currency].serie.find(m => m.mes === mes)
      return {
        mes: mes.split('-')[1],
        valor: found?.valor || 0
      }
    })
  }, [dashboard])
  
  // Get available currencies from dashboard
  const availableCurrencies = useMemo(() => {
    if (!dashboard?.por_moneda) return []
    return Object.keys(dashboard.por_moneda).filter(c => 
      dashboard.por_moneda[c].total !== 0 || 
      dashboard.por_moneda[c].mes !== 0 || 
      dashboard.por_moneda[c].anio !== 0 ||
      dashboard.cuentas_por_categoria?.some(cat => 
        cat.cuentas.some(acc => acc.moneda === c)
      )
    )
  }, [dashboard])
  
  // Save exchange rate
  const handleSaveExchangeRate = async () => {
    const rate = parseFloat(exchangeRate.usd_to_mxn)
    if (!rate || rate <= 0) {
      toast.error(isSpanish ? 'Ingresa un tipo de cambio válido' : 'Enter a valid exchange rate')
      return
    }
    
    try {
      await axios.put(`${API}/api/investments/exchange-rate`, {
        usd_to_mxn: rate,
        moneda_base: exchangeRate.moneda_base
      })
      toast.success(isSpanish ? 'Tipo de cambio actualizado' : 'Exchange rate updated')
      setShowExchangeModal(false)
      fetchDashboard() // Refresh to update consolidated view
    } catch (error) {
      toast.error(isSpanish ? 'Error al guardar' : 'Error saving')
    }
  }
  
  // Category handlers
  const handleSaveCategory = async () => {
    if (!categoryForm.nombre.trim()) {
      toast.error(isSpanish ? 'El nombre es requerido' : 'Name is required')
      return
    }
    
    try {
      if (editingCategory) {
        await axios.put(`${API}/api/investments/categories/${editingCategory.id}`, categoryForm)
        toast.success(isSpanish ? 'Categoría actualizada' : 'Category updated')
      } else {
        await axios.post(`${API}/api/investments/categories`, categoryForm)
        toast.success(isSpanish ? 'Categoría creada' : 'Category created')
      }
      setShowCategoryModal(false)
      setEditingCategory(null)
      setCategoryForm({ nombre: '' })
      fetchCategories()
      fetchDashboard()
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error' : 'Error'))
    }
  }
  
  const handleDeleteCategory = async (id) => {
    if (!window.confirm(isSpanish ? '¿Eliminar esta categoría?' : 'Delete this category?')) return
    try {
      await axios.delete(`${API}/api/investments/categories/${id}`)
      toast.success(isSpanish ? 'Categoría eliminada' : 'Category deleted')
      fetchCategories()
      fetchDashboard()
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error' : 'Error'))
    }
  }
  
  // Account handlers
  const handleSaveAccount = async () => {
    if (!accountForm.nombre.trim() || !accountForm.categoria_id) {
      toast.error(isSpanish ? 'Nombre y categoría son requeridos' : 'Name and category are required')
      return
    }
    
    try {
      if (editingAccount) {
        await axios.put(`${API}/api/investments/accounts/${editingAccount.id}`, accountForm)
        toast.success(isSpanish ? 'Cuenta actualizada' : 'Account updated')
      } else {
        await axios.post(`${API}/api/investments/accounts`, accountForm)
        toast.success(isSpanish ? 'Cuenta creada' : 'Account created')
      }
      setShowAccountModal(false)
      setEditingAccount(null)
      setAccountForm({ nombre: '', categoria_id: '', moneda: 'MXN', nota: '' })
      fetchDashboard()
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error' : 'Error'))
    }
  }
  
  const handleDeleteAccount = async () => {
    if (!editingAccount) return
    
    if (!window.confirm(isSpanish 
      ? '¿Eliminar esta cuenta? Se perderán todos los registros de rendimiento asociados.'
      : 'Delete this account? All associated profit records will be lost.')) {
      return
    }
    
    try {
      await axios.delete(`${API}/api/investments/accounts/${editingAccount.id}`)
      toast.success(isSpanish ? 'Cuenta eliminada' : 'Account deleted')
      setShowAccountModal(false)
      setEditingAccount(null)
      setAccountForm({ nombre: '', categoria_id: '', moneda: 'MXN', nota: '' })
      fetchDashboard()
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error al eliminar' : 'Error deleting'))
    }
  }
  
  const formatProfit = (value, currency = 'MXN') => {
    return formatCurrencyDisplay(value, currency, true)
  }
  
  const formatValue = (value, currency = 'MXN') => {
    return formatCurrencyDisplay(value, currency, false)
  }
  
  if (loading) {
    return (
      <div className="p-4 md:p-8 pb-safe flex items-center justify-center min-h-[50vh]">
        <div className="animate-pulse text-muted-foreground">
          {isSpanish ? 'Cargando...' : 'Loading...'}
        </div>
      </div>
    )
  }
  
  return (
    <div className="p-4 md:p-8 pb-safe max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">
            {isSpanish ? 'Inversiones' : 'Investments'}
          </h1>
          <p className="text-muted-foreground text-sm">
            {isSpanish ? 'Rendimiento mensual de tu portafolio' : 'Monthly portfolio performance'}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-investments-btn"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'separated' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('separated')}
          >
            {isSpanish ? 'Por Moneda' : 'By Currency'}
          </Button>
          <Button
            variant={viewMode === 'consolidated' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('consolidated')}
          >
            {isSpanish ? 'Consolidado' : 'Consolidated'}
          </Button>
        </div>
        {viewMode === 'consolidated' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowExchangeModal(true)}
            className="text-xs"
          >
            <Settings className="w-3 h-3 mr-1" />
            1 USD = {exchangeRate.usd_to_mxn} MXN
          </Button>
        )}
      </div>
      
      {/* SEPARATED VIEW: Summary per Currency */}
      {viewMode === 'separated' && (
        <div className="space-y-6 mb-6">
          {availableCurrencies.map(currency => {
            const data = dashboard?.por_moneda?.[currency] || {}
            const sparkData = getSparklineForCurrency(currency)
            const currencyColor = currency === 'USD' ? 'blue' : currency === 'MXN' ? 'emerald' : 'purple'
            
            return (
              <div key={currency} className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full bg-${currencyColor}-500`}></span>
                  {isSpanish ? `Portafolio ${currency}` : `${currency} Portfolio`}
                </h3>
                
                {/* Currency Summary Cards - 4 cards */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Card 1: Valor Total */}
                  <Card className={`bg-gradient-to-br from-${currencyColor}-500/10 to-${currencyColor}-600/5 border-${currencyColor}-500/20`}>
                    <CardContent className="p-3">
                      <div className={`flex items-center gap-1 text-${currencyColor}-600 mb-1`}>
                        <Wallet className="w-3 h-3" />
                        <span className="text-xs font-medium">{isSpanish ? 'Valor Total' : 'Total Value'}</span>
                      </div>
                      <p className="text-lg font-bold font-mono">
                        {formatValue(data.valor_total || 0, currency)}
                      </p>
                    </CardContent>
                  </Card>
                  
                  {/* Card 2: Rendimiento del Mes */}
                  <Card className="bg-secondary/30">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-1 text-muted-foreground mb-1">
                        <BarChart3 className="w-3 h-3" />
                        <span className="text-xs font-medium">{isSpanish ? 'Rend. Mes' : 'Month'}</span>
                      </div>
                      <p className={`text-lg font-bold font-mono ${(data.rendimiento_mes || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {formatProfit(data.rendimiento_mes || 0, currency)}
                      </p>
                    </CardContent>
                  </Card>
                  
                  {/* Card 3: Rendimiento del Año */}
                  <Card className="bg-secondary/30">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-1 text-muted-foreground mb-1">
                        <TrendingUp className="w-3 h-3" />
                        <span className="text-xs font-medium">{isSpanish ? 'Rend. Año' : 'Year'}</span>
                      </div>
                      <p className={`text-lg font-bold font-mono ${(data.rendimiento_anio || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {formatProfit(data.rendimiento_anio || 0, currency)}
                      </p>
                    </CardContent>
                  </Card>
                  
                  {/* Card 4: Rendimiento Acumulado */}
                  <Card className="bg-secondary/30">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-1 text-muted-foreground mb-1">
                        <DollarSign className="w-3 h-3" />
                        <span className="text-xs font-medium">{isSpanish ? 'Acumulado' : 'Cumulative'}</span>
                      </div>
                      <p className={`text-lg font-bold font-mono ${(data.rendimiento_acumulado || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {formatProfit(data.rendimiento_acumulado || 0, currency)}
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Currency Sparkline - Portfolio Value Evolution */}
                {sparkData.length > 0 && sparkData.some(d => d.valor > 0) && (
                  <Card>
                    <CardContent className="p-3">
                      <p className="text-xs text-muted-foreground mb-2">
                        {isSpanish ? 'Evolución del Valor' : 'Value Evolution'}
                      </p>
                      <div className="h-16">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={sparkData}>
                            <XAxis 
                              dataKey="mes" 
                              tick={{ fontSize: 8 }}
                              axisLine={false}
                              tickLine={false}
                            />
                            <YAxis hide />
                            <Tooltip 
                              formatter={(value) => [formatValue(value, currency), isSpanish ? 'Valor' : 'Value']}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="valor" 
                              stroke={currency === 'USD' ? '#3b82f6' : currency === 'MXN' ? '#10b981' : '#8b5cf6'}
                              strokeWidth={2}
                              dot={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )
          })}
          
          {availableCurrencies.length === 0 && (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">
                {isSpanish ? 'No hay datos de rendimiento aún' : 'No profit data yet'}
              </p>
            </Card>
          )}
        </div>
      )}
      
      {/* CONSOLIDATED VIEW: Single currency total */}
      {viewMode === 'consolidated' && (
        <>
          {/* Summary Cards - 4 cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {/* Card 1: Valor Total */}
            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
              <CardContent className="p-3">
                <div className="flex items-center gap-1 text-blue-600 mb-1">
                  <Wallet className="w-3 h-3" />
                  <span className="text-xs font-medium">
                    {isSpanish ? 'Valor Total' : 'Total Value'}
                  </span>
                </div>
                <p className="text-lg font-bold font-mono">
                  {formatValue(dashboard?.valor_total_consolidado || 0, exchangeRate.moneda_base)}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {isSpanish ? `En ${exchangeRate.moneda_base}` : `In ${exchangeRate.moneda_base}`}
                </p>
              </CardContent>
            </Card>
            
            {/* Card 2: Rendimiento Mes */}
            <Card className="bg-secondary/30">
              <CardContent className="p-3">
                <div className="flex items-center gap-1 text-muted-foreground mb-1">
                  <BarChart3 className="w-3 h-3" />
                  <span className="text-xs font-medium">
                    {isSpanish ? 'Rend. Mes' : 'Month'}
                  </span>
                </div>
                <p className={`text-lg font-bold font-mono ${(dashboard?.ganancia_mes_portafolio || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {formatProfit(dashboard?.ganancia_mes_portafolio || 0, exchangeRate.moneda_base)}
                </p>
              </CardContent>
            </Card>
            
            {/* Card 3: Rendimiento Año */}
            <Card className="bg-secondary/30">
              <CardContent className="p-3">
                <div className="flex items-center gap-1 text-muted-foreground mb-1">
                  <TrendingUp className="w-3 h-3" />
                  <span className="text-xs font-medium">
                    {isSpanish ? 'Rend. Año' : 'Year'}
                  </span>
                </div>
                <p className={`text-lg font-bold font-mono ${(dashboard?.ganancia_anio_portafolio || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {formatProfit(dashboard?.ganancia_anio_portafolio || 0, exchangeRate.moneda_base)}
                </p>
              </CardContent>
            </Card>
            
            {/* Card 4: Rendimiento Acumulado */}
            <Card className="bg-secondary/30">
              <CardContent className="p-3">
                <div className="flex items-center gap-1 text-muted-foreground mb-1">
                  <DollarSign className="w-3 h-3" />
                  <span className="text-xs font-medium">
                    {isSpanish ? 'Acumulado' : 'Cumulative'}
                  </span>
                </div>
                <p className={`text-lg font-bold font-mono ${(dashboard?.ganancia_total_portafolio || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {formatProfit(dashboard?.ganancia_total_portafolio || 0, exchangeRate.moneda_base)}
                </p>
              </CardContent>
            </Card>
          </div>
          
          {/* Sparkline Chart - Portfolio Value Evolution */}
          {sparklineData.length > 0 && sparklineData.some(d => d.valor > 0) && (
            <Card className="mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {isSpanish ? `Evolución del Valor (${new Date().getFullYear()})` : `Value Evolution (${new Date().getFullYear()})`}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-24">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sparklineData}>
                      <XAxis 
                        dataKey="mes" 
                        tick={{ fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis hide />
                      <Tooltip 
                        formatter={(value) => [formatValue(value, exchangeRate.moneda_base), isSpanish ? 'Valor' : 'Value']}
                        labelFormatter={(label) => {
                          const monthNames = isSpanish 
                            ? ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
                            : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                          return monthNames[parseInt(label) - 1]
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="valor" 
                        stroke="#10b981" 
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
      
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button 
          onClick={() => navigate('/inversiones/rendimiento')}
          className="flex-1 md:flex-none"
          data-testid="register-profit-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {isSpanish ? 'Registrar Rendimiento' : 'Record Profit'}
        </Button>
        <Button 
          variant="outline"
          onClick={() => navigate('/inversiones/historial')}
          data-testid="history-btn"
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          {isSpanish ? 'Historial' : 'History'}
        </Button>
        <Button 
          variant="outline"
          onClick={() => navigate('/inversiones/recordatorio')}
          data-testid="reminder-settings-btn"
        >
          <Settings className="w-4 h-4 mr-2" />
          {isSpanish ? 'Recordatorio' : 'Reminder'}
        </Button>
      </div>
      
      {/* Accounts by Category */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {isSpanish ? 'Cuentas' : 'Accounts'}
          </h2>
          <div className="flex gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setEditingCategory(null)
                setCategoryForm({ nombre: '' })
                setShowCategoryModal(true)
              }}
            >
              <Plus className="w-4 h-4 mr-1" />
              {isSpanish ? 'Categoría' : 'Category'}
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setEditingAccount(null)
                setAccountForm({ nombre: '', categoria_id: '', moneda: 'MXN', nota: '' })
                setShowAccountModal(true)
              }}
              data-testid="add-account-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              {isSpanish ? 'Cuenta' : 'Account'}
            </Button>
          </div>
        </div>
        
        {dashboard?.cuentas_por_categoria?.length === 0 ? (
          <Card className="p-8 text-center">
            <Wallet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {isSpanish 
                ? 'No hay cuentas configuradas. Crea tu primera cuenta de inversión.'
                : 'No accounts configured. Create your first investment account.'}
            </p>
            <Button 
              className="mt-4"
              onClick={() => setShowAccountModal(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              {isSpanish ? 'Crear Cuenta' : 'Create Account'}
            </Button>
          </Card>
        ) : (
          dashboard?.cuentas_por_categoria?.map((group) => (
            <Card key={group.categoria_id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {group.categoria_nombre}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const cat = categories.find(c => c.id === group.categoria_id)
                      if (cat) {
                        setEditingCategory(cat)
                        setCategoryForm({ nombre: cat.nombre })
                        setShowCategoryModal(true)
                      }
                    }}
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="divide-y">
                  {group.cuentas.map((cuenta) => (
                    <div 
                      key={cuenta.id}
                      className="py-3 -mx-4 px-4 transition-colors"
                      data-testid={`account-row-${cuenta.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        {/* Left side: name, currency, closing, sparkline */}
                        <div 
                          className="flex-1 min-w-0 cursor-pointer hover:bg-secondary/50 rounded-md p-1 -m-1"
                          onClick={() => navigate(`/inversiones/rendimiento?cuenta=${cuenta.id}`)}
                        >
                          <p className="font-medium truncate">{cuenta.nombre}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{cuenta.moneda}</span>
                            {cuenta.ultimo_cierre && (
                              <>
                                <span>•</span>
                                <span className="font-mono">
                                  {formatValue(cuenta.ultimo_cierre, cuenta.moneda)}
                                </span>
                              </>
                            )}
                          </div>
                          
                          {/* Mini Sparkline */}
                          {cuenta.sparkline && cuenta.sparkline.length > 1 ? (
                            <div className="h-12 w-full max-w-[140px] mt-2">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={cuenta.sparkline} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                                  <YAxis 
                                    domain={[
                                      dataMin => Math.floor(dataMin * 0.98),
                                      dataMax => Math.ceil(dataMax * 1.02)
                                    ]} 
                                    hide 
                                  />
                                  <Line 
                                    type="monotone" 
                                    dataKey="valor" 
                                    stroke={cuenta.moneda === 'USD' ? '#3b82f6' : '#10b981'}
                                    strokeWidth={2.5}
                                    dot={false}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          ) : cuenta.sparkline && cuenta.sparkline.length === 1 ? (
                            <div className="h-12 w-full max-w-[140px] mt-2 flex items-center">
                              <div className="w-full h-0.5 bg-muted-foreground/30 rounded"></div>
                            </div>
                          ) : null}
                        </div>
                        
                        {/* Right side: profits + actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <div 
                            className="cursor-pointer hover:bg-secondary/50 px-2 py-1 rounded-md text-right"
                            onClick={() => navigate(`/inversiones/rendimiento?cuenta=${cuenta.id}`)}
                          >
                            {cuenta.ganancia_mes_actual !== 0 || cuenta.ganancia_anio_actual !== 0 ? (
                              <>
                                <p className={`font-semibold text-sm font-mono ${cuenta.ganancia_mes_actual >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                  {formatProfit(cuenta.ganancia_mes_actual, cuenta.moneda)}
                                  <span className="text-[10px] font-normal ml-0.5 text-muted-foreground font-sans">
                                    {isSpanish ? 'mes' : 'mo'}
                                  </span>
                                </p>
                                <p className="text-[10px] text-muted-foreground font-mono">
                                  {isSpanish ? 'año:' : 'yr:'} {formatProfit(cuenta.ganancia_anio_actual, cuenta.moneda)}
                                </p>
                              </>
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                {isSpanish ? 'Sin rend.' : 'No profit'}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={(e) => {
                              e.stopPropagation()
                              setEditingAccount(cuenta)
                              setAccountForm({
                                nombre: cuenta.nombre,
                                categoria_id: cuenta.categoria_id || group.categoria_id,
                                moneda: cuenta.moneda,
                                nota: cuenta.nota || ''
                              })
                              setShowAccountModal(true)
                            }}
                            data-testid={`edit-account-${cuenta.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <ChevronRight 
                            className="w-4 h-4 text-muted-foreground cursor-pointer" 
                            onClick={() => navigate(`/inversiones/rendimiento?cuenta=${cuenta.id}`)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      
      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory 
                ? (isSpanish ? 'Editar Categoría' : 'Edit Category')
                : (isSpanish ? 'Nueva Categoría' : 'New Category')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isSpanish ? 'Nombre' : 'Name'}</Label>
              <Input
                value={categoryForm.nombre}
                onChange={(e) => setCategoryForm({ ...categoryForm, nombre: e.target.value })}
                placeholder={isSpanish ? 'Ej: Acciones' : 'E.g., Stocks'}
                data-testid="category-name-input"
              />
            </div>
            <div className="flex gap-2">
              {editingCategory && (
                <Button
                  variant="destructive"
                  onClick={() => {
                    handleDeleteCategory(editingCategory.id)
                    setShowCategoryModal(false)
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {isSpanish ? 'Eliminar' : 'Delete'}
                </Button>
              )}
              <Button 
                className="flex-1"
                onClick={handleSaveCategory}
                data-testid="save-category-btn"
              >
                {isSpanish ? 'Guardar' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Account Modal */}
      <Dialog open={showAccountModal} onOpenChange={setShowAccountModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAccount 
                ? (isSpanish ? 'Editar Cuenta' : 'Edit Account')
                : (isSpanish ? 'Nueva Cuenta' : 'New Account')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isSpanish ? 'Nombre' : 'Name'}</Label>
              <Input
                value={accountForm.nombre}
                onChange={(e) => setAccountForm({ ...accountForm, nombre: e.target.value })}
                placeholder={isSpanish ? 'Ej: Apple, VOO, Nu Cajita' : 'E.g., Apple, VOO, Nu Savings'}
                data-testid="account-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{isSpanish ? 'Categoría' : 'Category'}</Label>
              <Select
                value={accountForm.categoria_id}
                onValueChange={(v) => setAccountForm({ ...accountForm, categoria_id: v })}
              >
                <SelectTrigger data-testid="account-category-select">
                  <SelectValue placeholder={isSpanish ? 'Seleccionar...' : 'Select...'} />
                </SelectTrigger>
                <SelectContent>
                  {categories.filter(c => c.activo).map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{isSpanish ? 'Moneda' : 'Currency'}</Label>
              <Select
                value={accountForm.moneda}
                onValueChange={(v) => setAccountForm({ ...accountForm, moneda: v })}
              >
                <SelectTrigger data-testid="account-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MXN">MXN</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{isSpanish ? 'Nota (opcional)' : 'Note (optional)'}</Label>
              <Input
                value={accountForm.nota || ''}
                onChange={(e) => setAccountForm({ ...accountForm, nota: e.target.value })}
                placeholder={isSpanish ? 'Descripción breve...' : 'Brief description...'}
              />
            </div>
            <div className={`flex gap-2 ${editingAccount ? '' : ''}`}>
              {editingAccount && (
                <Button 
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  data-testid="delete-account-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {isSpanish ? 'Eliminar' : 'Delete'}
                </Button>
              )}
              <Button 
                className="flex-1"
                onClick={handleSaveAccount}
                data-testid="save-account-btn"
              >
                {isSpanish ? 'Guardar' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Exchange Rate Modal */}
      <Dialog open={showExchangeModal} onOpenChange={setShowExchangeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isSpanish ? 'Configurar Tipo de Cambio' : 'Configure Exchange Rate'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isSpanish ? 'Moneda Base' : 'Base Currency'}</Label>
              <Select
                value={exchangeRate.moneda_base}
                onValueChange={(v) => setExchangeRate({ ...exchangeRate, moneda_base: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MXN">MXN (Peso Mexicano)</SelectItem>
                  <SelectItem value="USD">USD (Dólar)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {isSpanish 
                  ? 'Todos los valores se convertirán a esta moneda en la vista consolidada'
                  : 'All values will be converted to this currency in consolidated view'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>1 USD = ? MXN</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={exchangeRate.usd_to_mxn}
                onChange={(e) => {
                  const val = e.target.value
                  // Allow empty string or valid numbers
                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                    setExchangeRate({ ...exchangeRate, usd_to_mxn: val })
                  }
                }}
                className="font-mono text-lg"
                placeholder="17.77"
              />
              <p className="text-xs text-muted-foreground">
                {isSpanish 
                  ? 'Ingresa el tipo de cambio actual para convertir entre USD y MXN'
                  : 'Enter current exchange rate to convert between USD and MXN'}
              </p>
            </div>
            
            <Button 
              className="w-full"
              onClick={handleSaveExchangeRate}
              disabled={!exchangeRate.usd_to_mxn || parseFloat(exchangeRate.usd_to_mxn) <= 0}
            >
              {isSpanish ? 'Guardar' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
